// utils/PackedCircleCustomOption.js
import { hierarchy, pack } from 'd3-hierarchy'

const PAL = ['#4F46E5', '#10B981', '#F59E0B', '#EC4899', '#14B8A6', '#8B5CF6', '#EF4444', '#0EA5E9']

/**
 * Build a *true* packed-circle option that always fits.
 * @param clusters
 * @param {number} w  container width  (px) – pass the same you give the chart
 * @param {number} h  container height (px)
 */
export function buildPackedCircleOption(clusters, w = 400, h = 400) {
    if (!clusters?.length) return { title: { text: 'No cluster data' } }

    /* 1 ─ D3 pack in a virtual 1000×1000 box */
    const BOX = 1000
    const root = hierarchy({ children: clusters })
        .sum((d) => d.count)
        .sort((a, b) => b.value - a.value)

    pack().size([BOX, BOX]).padding(4)(root)

    /* 2 ─ collect leaves & find bounding box (includes radius) */
    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity
    const leaves = root.leaves().map((leaf, i) => {
        const o = {
            name: leaf.data.clusterName || `Cluster ${leaf.data.clusterID}`,
            desc: leaf.data.clusterDescription || '',
            value: leaf.data.count,
            cx: leaf.x,
            cy: leaf.y,
            r: leaf.r,
            color: PAL[i % PAL.length],
            attributes: leaf.data.attributes,
            queryFilter: leaf.data.queryFilter,
            clusterID: leaf.data.clusterID,
        }
        minX = Math.min(minX, o.cx - o.r)
        minY = Math.min(minY, o.cy - o.r)
        maxX = Math.max(maxX, o.cx + o.r)
        maxY = Math.max(maxY, o.cy + o.r)
        return o
    })

    /* 3 ─ compute scale & offsets so bubbles fit the actual canvas */
    const pad = 4 // px margin
    const boxW = maxX - minX
    const boxH = maxY - minY
    const scale = Math.min((w - 2 * pad) / boxW, (h - 2 * pad) / boxH)

    /* 4 ─ final data array with real px coords */
    const data = leaves.map((d) => ({
        ...d,
        cx: (d.cx - minX) * scale + pad,
        cy: (d.cy - minY) * scale + pad,
        r: d.r * scale,
    }))

    function wrap(text, maxLen = 16) {
        if (text.length <= maxLen) return text

        const words = text.split(' ')
        const lines = []
        let buf = ''
        let truncated = false

        for (let i = 0; i < words.length; i++) {
            const w = words[i]
            const cand = (buf + ' ' + w).trim()

            if (cand.length > maxLen && buf) {
                // push full line
                lines.push(buf)
                buf = w
            } else {
                buf = cand
            }

            /* already have 2 full lines and there are still words left */
            if (lines.length === 2 && i < words.length - 1) {
                buf += '…' // mark truncation
                truncated = true
                break // stop processing
            }
        }
        lines.push(buf) // last line

        // If nothing was truncated we may have ≤2 lines; that's fine.
        return lines.slice(0, 3).join('\n')
    }

    /* 6 ─ renderItem */
    function renderItem(p /*, api */) {
        const d = data[p.dataIndex]
        return {
            type: 'circle',
            shape: { cx: d.cx, cy: d.cy, r: d.r },
            style: { fill: d.color, opacity: 0.85 },
            textContent: {
                type: 'text',
                style: {
                    text: wrap(d.name),
                    overflow: 'break',
                    width: d.r * 1.6,
                    fontSize: Math.max(d.r / 5, 8),
                    lineHeight: Math.max(d.r / 4, 10),
                    fontWeight: 600,
                    align: 'center',
                    verticalAlign: 'middle',
                    fill: '#fff',
                },
            },
            textConfig: { position: 'inside' },
            emphasis: {
                style: { shadowBlur: 18, shadowColor: 'rgba(0,0,0,0.25)' },
            },
        }
    }

    /* 7 ─ option */
    return {
        tooltip: {
            trigger: 'item',
            position: (pos, params, dom, rect, size) => {
                // params.data has the circle pixel coords we computed earlier
                const { cx, cy, r } = params.data
                const { contentSize } = size // [w, h]
                return [cx - contentSize[0] / 2, cy]
            },
            formatter: (p) =>
                `<div style="max-width:240px;text-align:center;white-space:normal;">
        <b>${p.data.name}</b><br/>
        <span>Count: ${(p.data.value ?? 0).toLocaleString()}</span><br/>
        <span>${p.data.desc}</span>
      </div>`,
        },
        series: [
            {
                type: 'custom',
                coordinateSystem: 'none',
                renderItem,
                data,
                progressive: 0,
            },
        ],
        backgroundColor: 'transparent',
        animation: true,
    }
}
